// src/utils/comfortDensity.js
//
// OFFICES-SPACE-FIT-TO-COMFORT-DENSITY: Replace the multi-factor Space Fit
// score with a simple density-based "Comfort" metric: rentable SF ÷
// installed seats, banded into Roomy / Tight / Crowded.
//
// This intentionally does NOT use location.sqft directly — callers must
// pass the same derived rentable SF the SQ FT column already shows (via
// getDisplayedSqft/deriveLocationSqft in locationSqftDerived.js), so
// serviced offices with junk/null location.sqft (e.g. Sydney·The Hub)
// still get a sane Comfort reading.
//
// Thresholds are named constants in one place so a future admin-tunable
// pass can pick them up easily.

export const COMFORT_ROOMY_MIN_SF_PER_SEAT = 250;
export const COMFORT_TIGHT_MIN_SF_PER_SEAT = 150;

export const COMFORT_BAND_ROOMY = "roomy";
export const COMFORT_BAND_TIGHT = "tight";
export const COMFORT_BAND_CROWDED = "crowded";

// Severity order — used for "sort by band severity" (Roomy → Tight → Crowded).
export const COMFORT_BAND_ORDER = {
  [COMFORT_BAND_ROOMY]: 0,
  [COMFORT_BAND_TIGHT]: 1,
  [COMFORT_BAND_CROWDED]: 2,
};

export const COMFORT_BAND_LABEL = {
  [COMFORT_BAND_ROOMY]: "Roomy",
  [COMFORT_BAND_TIGHT]: "Tight",
  [COMFORT_BAND_CROWDED]: "Crowded",
};

/**
 * Compute the Comfort band + raw SF/seat ratio for a location.
 *
 * @param {number|null} rentableSf  Derived rentable SF (getDisplayedSqft output) — never location.sqft directly.
 * @param {number|null} seats       Installed seats (loc.installedCapacity).
 * @returns {{ band: string|null, ratio: number|null }}
 *   band is null and ratio is null when there's no usable SF or seats = 0.
 */
export function computeComfort(rentableSf, seats) {
  const sf = rentableSf != null ? Number(rentableSf) : null;
  const seatCount = seats != null ? Number(seats) : null;

  if (sf == null || !isFinite(sf) || seatCount == null || !isFinite(seatCount) || seatCount <= 0) {
    return { band: null, ratio: null };
  }

  const ratio = sf / seatCount;
  if (!isFinite(ratio)) return { band: null, ratio: null };

  let band;
  if (ratio >= COMFORT_ROOMY_MIN_SF_PER_SEAT) band = COMFORT_BAND_ROOMY;
  else if (ratio >= COMFORT_TIGHT_MIN_SF_PER_SEAT) band = COMFORT_BAND_TIGHT;
  else band = COMFORT_BAND_CROWDED;

  return { band, ratio };
}
